import { Fragment, useMemo, useState } from "react";
import { useAtomValue } from "jotai";
import {
  Calendar,
  ChevronDown,
  ChevronRight,
  DollarSign,
  FileText,
  Percent,
  Target,
  TrendingUp,
} from "lucide-react";
import ReactECharts from "echarts-for-react";
import type { EChartsOption } from "echarts";
import type { GeoPlanItem, GeoPlanRevenue } from "@monitoring-panel/types";
import { useGeoRevenue } from "@/hooks/useRevenue";
import { formatDate, formatNumber } from "@/lib/utils";
import { MetricBox } from "@/components/MetricBox";
import { HoldingsBarChart } from "@/components/HoldingsBarChart";
import { HoldingsSunburstChart } from "@/components/HoldingsSunburstChart";
import { RevenueDateRangeBar } from "@/components/revenue/RevenueDateRangeBar";
import { revenueDateRangeAtom } from "@/store/revenue";
import {
  ADVIA_CHART_OPTS,
  ADVIA_ECHARTS_THEME,
  GEO_PLAN_COLORS,
  solid,
  tooltipRow,
  tooltipTitle,
} from "@/lib/advia-charts";

const GEO_CHART_COLORS = {
  mediaPlan: solid(GEO_PLAN_COLORS.mediaPlan),
  analytics: solid(GEO_PLAN_COLORS.analytics),
  mediaPlanForecast: solid(GEO_PLAN_COLORS.mediaPlanForecast),
  analyticsForecast: solid(GEO_PLAN_COLORS.analyticsForecast),
} as const;

const PLAN_KIND_LABELS: Record<GeoPlanRevenue["kind"], string> = {
  media: "Media Plan",
  analytics: "Analytics Plan",
};

function marginPercentage(revenue: number, mediaCost: number): number {
  return revenue > 0 ? ((revenue - mediaCost) / revenue) * 100 : 0;
}

function targetColor(percentage: number): string {
  if (percentage >= 100) return "text-adv-success";
  if (percentage >= 80) return "text-adv-accent";
  return "text-adv-warning";
}

function PlanItemsTable({ plan }: { plan: GeoPlanRevenue }) {
  if (plan.spreadLinearly) {
    return (
      <p className="px-6 py-4 text-adv-body-s text-adv-secondary">
        No {plan.kind === "media" ? "articles" : "analyses"} yet: the budget is
        forecast evenly over the opportunity's remaining days
        {plan.opportunityStartDate && plan.opportunityEndDate
          ? ` (${formatDate(plan.opportunityStartDate)} → ${formatDate(plan.opportunityEndDate)})`
          : ""}
        .
      </p>
    );
  }

  return (
    <table className="adv-table adv-table--compact">
      <thead>
        <tr>
          <th className="px-4 py-2 text-left">ID</th>
          <th className="px-4 py-2 text-left">{plan.kind === "media" ? "Article" : "Analysis"}</th>
          {plan.kind === "media" && <th className="px-4 py-2 text-left">Publisher</th>}
          <th className="px-4 py-2 text-left">Status</th>
          <th className="px-4 py-2 text-center">Planned</th>
          <th className="px-4 py-2 text-center">Reported on</th>
          <th className="px-4 py-2 text-right">Value</th>
          {plan.kind === "media" && <th className="px-4 py-2 text-right">Cost</th>}
        </tr>
      </thead>
      <tbody>
        {plan.items.map((item: GeoPlanItem) => {
          const isMovedToToday = item.state === "forecast" && item.date !== item.plannedDate;
          return (
            <tr key={item.id}>
              <td className="px-4 py-2 adv-num whitespace-nowrap">{item.internalId}</td>
              <td className="px-4 py-2 max-w-xs truncate" title={item.name}>
                {item.name}
              </td>
              {plan.kind === "media" && (
                <td className="px-4 py-2 text-adv-secondary whitespace-nowrap">{item.publisher ?? "—"}</td>
              )}
              <td className="px-4 py-2 text-adv-secondary whitespace-nowrap">{item.status || "—"}</td>
              <td className="px-4 py-2 text-center adv-num text-adv-secondary whitespace-nowrap">
                {item.plannedDate ? formatDate(item.plannedDate) : "—"}
              </td>
              <td
                className={`px-4 py-2 text-center adv-num whitespace-nowrap ${
                  isMovedToToday ? "text-adv-warning-text font-bold" : "text-adv-secondary"
                }`}
                title={
                  isMovedToToday
                    ? "Planned date already passed (or missing) and still open: forecast moved to today"
                    : undefined
                }
              >
                {formatDate(item.date)}
              </td>
              <td className="px-4 py-2 text-right whitespace-nowrap">
                <span
                  className={`adv-num ${
                    item.state === "recognized" ? "font-bold text-adv-primary" : "text-adv-success"
                  }`}
                >
                  {formatNumber(item.value, "euro")}
                </span>
                <span className="ml-1 text-adv-label text-adv-tertiary">
                  {item.state === "recognized" ? "actual" : "forecast"}
                </span>
              </td>
              {plan.kind === "media" && (
                <td className="px-4 py-2 text-right adv-num text-adv-secondary whitespace-nowrap">
                  {item.cost === null ? (
                    <span className="text-adv-tertiary" title="No publisher purchase linked">
                      —
                    </span>
                  ) : (
                    formatNumber(item.cost, "euro")
                  )}
                </td>
              )}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function PlansTable({ plans }: { plans: GeoPlanRevenue[] }) {
  const [expandedPlanId, setExpandedPlanId] = useState<string | null>(null);

  return (
    <div className="adv-card adv-card--featured overflow-hidden">
      <div className="px-6 py-4 border-b border-adv-border">
        <h2 className="adv-section-title">Plans</h2>
        <p className="adv-section-desc mt-0.5">
          Confirmed media plans and approved analytics plans with revenue or forecast in the range
        </p>
      </div>
      <div className="overflow-x-auto">
        <table className="adv-table">
          <thead className="bg-adv-sunken">
            <tr>
              <th className="px-4 py-3 text-left">Plan</th>
              <th className="px-4 py-3 text-center">Type</th>
              <th className="px-4 py-3 text-left">Account</th>
              <th className="px-4 py-3 text-right">Budget</th>
              <th className="px-4 py-3 text-right">Actual</th>
              <th className="px-4 py-3 text-right">Forecasted</th>
              <th className="px-4 py-3 text-right">Cost</th>
              <th className="px-4 py-3 text-right">Margin</th>
              <th className="px-4 py-3 text-center">Done</th>
            </tr>
          </thead>
          <tbody>
            {plans.map((plan) => {
              const isExpanded = expandedPlanId === plan.id;
              const doneItems = plan.items.filter((item) => item.state === "recognized").length;
              return (
                <Fragment key={plan.id}>
                  <tr
                    className="hover:bg-adv-sunken cursor-pointer"
                    onClick={() => setExpandedPlanId(isExpanded ? null : plan.id)}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-start gap-2">
                        {isExpanded ? (
                          <ChevronDown className="text-adv-tertiary mt-1 shrink-0" size={16} />
                        ) : (
                          <ChevronRight className="text-adv-tertiary mt-1 shrink-0" size={16} />
                        )}
                        <div>
                          <span className="adv-num font-bold">{plan.internalId}</span>
                          <p className="text-adv-secondary max-w-xs truncate" title={plan.name}>
                            {plan.name}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center whitespace-nowrap">
                      <span className="adv-tag adv-tag--default">{PLAN_KIND_LABELS[plan.kind]}</span>
                    </td>
                    <td className="px-4 py-3">
                      <p className="max-w-xs truncate" title={plan.account}>
                        {plan.account}
                      </p>
                      <p className="text-adv-body-s text-adv-secondary">
                        {plan.agency} · {plan.holding}
                      </p>
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap adv-num">
                      {formatNumber(plan.budget, "euro")}
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap adv-num font-bold">
                      {formatNumber(plan.revenue, "euro")}
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap adv-num">
                      <span className={plan.forecastedRevenue > 0 ? "text-adv-success" : "text-adv-tertiary"}>
                        {formatNumber(plan.forecastedRevenue, "euro")}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap adv-num text-adv-secondary">
                      {plan.kind === "media" ? formatNumber(plan.mediaCost, "euro") : "—"}
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap adv-num text-adv-secondary">
                      {plan.kind === "media" && plan.revenue > 0
                        ? formatNumber(marginPercentage(plan.revenue, plan.mediaCost), "percentage", 1)
                        : "—"}
                    </td>
                    <td className="px-4 py-3 text-center whitespace-nowrap adv-num text-adv-secondary">
                      {plan.spreadLinearly ? "Linear" : `${doneItems} / ${plan.items.length}`}
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr>
                      <td colSpan={9} className="bg-adv-inset border-t border-adv-border p-0">
                        <PlanItemsTable plan={plan} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * GEO revenue: confirmed LLM media plans (revenue per published article) and
 * approved LLM analytics plans (revenue per delivered analysis).
 */
export function GeoRevenueView() {
  const { startDate, endDate } = useAtomValue(revenueDateRangeAtom);
  const [shouldFetch, setShouldFetch] = useState(false);
  const { data, isLoading, error } = useGeoRevenue(startDate, endDate, shouldFetch);

  const chartOption: EChartsOption = useMemo(() => {
    if (!data?.data || data.data.length === 0) return {};

    const series = [
      { name: "Media Plan", key: "mediaPlanRevenue", color: GEO_CHART_COLORS.mediaPlan },
      { name: "Analytics", key: "analyticsRevenue", color: GEO_CHART_COLORS.analytics },
      { name: "Media Plan Forecast", key: "mediaPlanForecast", color: GEO_CHART_COLORS.mediaPlanForecast },
      { name: "Analytics Forecast", key: "analyticsForecast", color: GEO_CHART_COLORS.analyticsForecast },
    ] as const;

    return {
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "shadow" },
        formatter: (params: unknown) => {
          const items = params as Array<{
            seriesName: string;
            value: number;
            marker: string;
            axisValue: string;
          }>;
          let result = tooltipTitle(items[0].axisValue);
          items.forEach((item) => {
            if (item.value > 0) {
              result += tooltipRow(item.seriesName, formatNumber(item.value, "euro"), item.marker);
            }
          });
          return result;
        },
      },
      grid: { right: 16, left: 16, bottom: 8, top: 48, containLabel: true },
      legend: {
        top: 0,
        left: "center",
        itemGap: 24,
      },
      xAxis: [
        {
          type: "category",
          data: data.data.map((row) => formatDate(row.date)),
          axisTick: { alignWithLabel: true },
          axisLabel: { margin: 10, hideOverlap: true },
        },
      ],
      yAxis: [
        {
          type: "value",
          name: "Revenue (€)",
          axisLabel: {
            formatter: (value: number) => formatNumber(value, "euro"),
          },
        },
      ],
      series: series.map(({ name, key, color }) => ({
        name,
        type: "bar",
        stack: "geo",
        barWidth: "90%",
        data: data.data.map((row) => row[key]),
        itemStyle: { color },
      })),
    };
  }, [data]);

  return (
    <div className="space-y-6">
      <RevenueDateRangeBar
        isLoading={isLoading}
        onLoad={() => setShouldFetch(true)}
        onRangeChange={() => setShouldFetch(false)}
        footer={data && `${data.plans.length} plans included in range`}
      />

      {error && (
        <div className="adv-alert">
          <p>Error: {(error as Error).message}</p>
        </div>
      )}

      {data && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <MetricBox
              title="Acc Revenue"
              value={data.summary.accumulatedRevenue}
              subtitle="Published articles and delivered analyses"
              format="euro"
              icon={DollarSign}
            />
            <MetricBox
              title="Revenue Estimated"
              value={data.summary.accumulatedPlusForecasted}
              subtitle={`Includes ${formatNumber(data.summary.totalForecasted, "euro")} forecasted`}
              format="euro"
              icon={TrendingUp}
            />
            <MetricBox
              title="Target vs Actual + Forecast"
              value={data.summary.targetPercentage}
              subtitle={`${formatNumber(data.summary.accumulatedPlusForecasted, "euro")} / ${formatNumber(data.summary.targetRevenue, "euro")}`}
              format="percentage"
              icon={Target}
              valueColor={targetColor(data.summary.targetPercentage)}
            />
            <MetricBox
              title="Media Plan Margin"
              value={marginPercentage(data.summary.mediaPlan.revenue, data.summary.mediaPlan.mediaCost)}
              subtitle={
                data.summary.mediaPlan.uncostedArticles > 0
                  ? `${data.summary.mediaPlan.uncostedArticles} published articles without purchase (no cost)`
                  : `Cost ${formatNumber(data.summary.mediaPlan.mediaCost, "euro")} on actual revenue`
              }
              format="percentage"
              icon={Percent}
            />
            <MetricBox
              title="Analytics Plans"
              value={data.summary.analytics.revenue + data.summary.analytics.forecasted}
              subtitle={`Actual: ${formatNumber(data.summary.analytics.revenue, "euro")} + Forecast: ${formatNumber(data.summary.analytics.forecasted, "euro")}`}
              format="euro"
              icon={FileText}
            />
          </div>

          {data.data.length > 0 && (
            <div className="adv-card p-6">
              <h2 className="adv-section-title mb-6">
                Daily GEO Revenue (Actual & Forecasted)
              </h2>
              <ReactECharts
                option={chartOption}
                style={{ height: 480 }}
                notMerge
                theme={ADVIA_ECHARTS_THEME}
                opts={ADVIA_CHART_OPTS}
              />
            </div>
          )}

          {data.plans.length > 0 ? (
            <PlansTable plans={data.plans} />
          ) : (
            <div className="adv-card adv-state">
              <div className="adv-state__icon">
                <DollarSign size={26} />
              </div>
              <p className="adv-state__desc">No GEO plans with revenue in the selected date range</p>
            </div>
          )}

          {data.holdingsBreakdown.length > 0 && (
            <>
              <HoldingsBarChart data={data.holdingsBreakdown} />
              <HoldingsSunburstChart data={data.holdingsBreakdown} showForecasted={true} />
            </>
          )}
        </>
      )}

      {!shouldFetch && !data && (
        <div className="adv-card adv-state">
          <div className="adv-state__icon">
            <Calendar size={26} />
          </div>
          <p className="adv-state__desc">
            Select a date range and click "Load Data" to view revenue data
          </p>
        </div>
      )}
    </div>
  );
}
